import dayjs from 'dayjs';
import {
  BaseEntity,
  JsonApiProperty,
  JsonApiRelationship,
  JsonApiResource,
} from '../jsonapi-provider';
import { LocalDate } from '../utils/local-date';
import type Agency from './Agency';
import type Customer from './customer';
import type CycleMenuBeverage from './cycle-menu-beverage';
import type CycleMenuSupply from './cycle-menu-supply';
import type ScheduledMeal from './scheduled-meal';
import type Site from './site';

interface SitesGroup {
  agencyId: Agency['id'];
  state?: string;
  county?: string;
  customerId?: Customer['id'];
}

@JsonApiResource({
  type: 'cycle-menu',
})
export default class CycleMenu extends BaseEntity {
  name: string = '';
  @JsonApiProperty({ resolver: (val: any) => new LocalDate(val) }) startDate = new LocalDate();
  @JsonApiProperty({ resolver: (val: any) => new LocalDate(val) }) endDate = new LocalDate().add(
    1,
    'd'
  );

  @JsonApiProperty({ resolver: (val) => (val ? new LocalDate(val) : null) })
  repeatUntil: LocalDate | null = null;

  // should be removed before sending to backend, only used for frontend logic
  @JsonApiProperty({ type: 'object' }) sitesGroups = new Array<SitesGroup>();

  isEnabled = true;
  // should be removed before sending to backend, only used for frontend logic
  @JsonApiRelationship('agency') agencies = new Array<Agency>();
  @JsonApiRelationship('site') sites = new Array<Site>();
  @JsonApiRelationship('scheduled-meal') scheduledMeals = new Array<ScheduledMeal>();

  @JsonApiRelationship('cycle-menu-beverage') cycleMenuBeverages = new Array<CycleMenuBeverage>();

  @JsonApiRelationship('cycle-menu-supply') cycleMenuSupplies = new Array<CycleMenuSupply>();

  clonedCycleMenuId: string | undefined = undefined;

  get cycleLength(): number {
    return this.endDate.add(1, 'd').diff(this.startDate, 'd');
  }

  get repeats(): Array<{ start: LocalDate; end: LocalDate }> {
    if (this.repeatUntil == null) return [{ start: this.startDate, end: this.endDate }];

    const ret = [];
    for (let cur = this.startDate; cur <= this.repeatUntil; cur = cur.add(this.cycleLength, 'd')) {
      ret.push({
        start: cur,
        end: new LocalDate(
          dayjs.min(cur.add(this.cycleLength - 1, 'd').toDayjs(), this.repeatUntil.toDayjs())
        ),
      });
    }
    return ret;
  }

  constructor(
    id?: CycleMenu['id'],
    name: CycleMenu['name'] = '',
    startDate: CycleMenu['startDate'] = new LocalDate(),
    endDate: CycleMenu['endDate'] = new LocalDate().add(1, 'd'),
    repeatUntil: CycleMenu['repeatUntil'] = null,
    isEnabled: CycleMenu['isEnabled'] = true,
    sites: CycleMenu['sites'] = new Array<Site>(),
    scheduledMeals: CycleMenu['scheduledMeals'] = new Array<ScheduledMeal>(),
    cycleMenuBeverages: CycleMenu['cycleMenuBeverages'] = new Array<CycleMenuBeverage>(),
    cycleMenuSupplies: CycleMenu['cycleMenuSupplies'] = new Array<CycleMenuSupply>()
  ) {
    super();
    this.id = id;
    this.name = name;
    this.startDate = startDate;
    this.endDate = endDate;
    this.repeatUntil = repeatUntil;
    this.isEnabled = isEnabled;
    this.sites = sites;
    this.scheduledMeals = scheduledMeals;
    this.cycleMenuBeverages = cycleMenuBeverages;
    this.cycleMenuSupplies = cycleMenuSupplies;
  }

  getDateRange(): [LocalDate, LocalDate] {
    return [this.startDate, this.repeatUntil ?? this.endDate];
  }
}
